'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useTranslation as useTranslationQuery, useDeleteTranslations } from '@/lib/api/translations'
import { useToast } from '@/components/ui/use-toast'

interface DeleteTranslationModalProps {
	translationId?: string
	onClose: () => void
	onSuccess?: () => void
}

interface DeleteTranslationModalContentProps {
	translationId: string
	onClose: () => void
	onSuccess?: () => void
}

export const DeleteTranslationModal = ({ translationId, onClose, onSuccess }: DeleteTranslationModalProps) => {
	if (!translationId) return null
	return <DeleteTranslationModalContent translationId={translationId} onClose={onClose} onSuccess={onSuccess} />
}

const DeleteTranslationModalContent = ({ translationId, onClose, onSuccess }: DeleteTranslationModalContentProps) => {
	const { t } = useTranslation('languages')
	const { t: tCommon } = useTranslation('common')
	const { toast } = useToast()
	const { data: translationData, isPending: isLoadingTranslation } = useTranslationQuery(translationId)
	const deleteTranslations = useDeleteTranslations()

	const handleDelete = async () => {
		if (!translationId) return

		try {
			await deleteTranslations.mutateAsync({ ids: [translationId] })

			toast({
				title: tCommon('success'),
				description: t('deleteSuccess'),
			})

			onSuccess?.()
			onClose()
		} catch (error) {
			// Error toast is handled by the mutation hook
			// No need to show another error toast here
		}
	}

	const translationLocale = translationData?.translation?.locale || t('translation')

	return (
		<AlertDialog open={true} onOpenChange={onClose}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('modals.delete.title')}</AlertDialogTitle>
					<AlertDialogDescription>
						{isLoadingTranslation
							? t('modals.delete.loading')
							: t('modals.delete.description', { locale: translationLocale })}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<Button variant="outline" onClick={onClose} disabled={deleteTranslations.isPending}>
						{tCommon('cancel')}
					</Button>
					<Button
						variant="destructive"
						onClick={handleDelete}
						disabled={deleteTranslations.isPending || isLoadingTranslation}
					>
						{deleteTranslations.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{tCommon('delete')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

